import { MeasureType } from 'domains/measurementDomain';
import { OrientationEnum } from 'domains/skuDomain';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { useMemo } from 'react';
import { cubeOrganizeStructure, organizeStructure } from 'utils/filter';
import { roundSizeAxis } from 'utils/measurement';

interface Iprops {
  finishSize?: string;
  /**
   * The API's metric twin of `finishSize`.
   *
   * Required, not optional: a caller that omits it renders the imperial size under a metric
   * label - which is exactly what the job config size options were doing.
   */
  finishSizeMetric: string | null | undefined;
  isSpecialSku?: boolean;
  orientation?: OrientationEnum;
  isWorkspace?: boolean;
}

/**
 * Named card sizes, keyed by their imperial dimensions.
 *
 * The lookup stays imperial because that is what the stored size is - a metric viewer still sees
 * "A7", just with millimetres in the brackets.
 */
const NAMED_CARD_SIZES: { name: string; width: number; length: number }[] = [
  { name: 'A2', width: 4.375, length: 5.75 },
  { name: 'A6', width: 4.75, length: 6.5 },
  { name: 'A7', width: 5.25, length: 7.25 },
  { name: 'A8', width: 5.5, length: 8.125 },
  { name: 'A10', width: 6, length: 9.5 },
];

const namedSizeFor = (width: number, length: number) =>
  NAMED_CARD_SIZES.find(
    (size) =>
      (size.width === width && size.length === length) ||
      (size.width === length && size.length === width)
  )?.name;

/**
 * Renders a stored finished size in the viewer's measurement system.
 *
 * The size arrives as the inches string the API stores (`8.5x11`, or `10x8x4` for a cube). Only the
 * rendering changes with the unit - nothing here is ever fed back as a form value or sent to the API.
 */
export const RevxRenderFinishSize = ({
  finishSize,
  finishSizeMetric,
  isSpecialSku,
  orientation,
  isWorkspace,
}: Iprops) => {
  const { symbol, sizeFor } = useMeasurementUnit();
  const isCube = orientation === OrientationEnum.cube;

  const { width, length, height } = useMemo(
    () =>
      isCube
        ? cubeOrganizeStructure(sizeFor(finishSize, finishSizeMetric) || '')
        : { ...organizeStructure(sizeFor(finishSize, finishSizeMetric) || ''), height: 0 },
    [isCube, finishSize, finishSizeMetric, sizeFor]
  );

  const xStyle = useMemo(() => {
    return isWorkspace ? {} : { padding: '0 4px' };
  }, [isWorkspace]);

  const unit = symbol(MeasureType.LENGTH);
  // Read to a size's own precision rather than the general one, so this and `displaySize` - which
  // sit beside each other in the cart - do not disagree about a fraction of an inch.
  const shown = (value: number) => `${roundSizeAxis(value) ?? 0}${unit}`;
  const separator = <span style={xStyle}> x </span>;

  // The name is matched on the stored imperial dimensions; the brackets follow the viewer's unit.
  const namedSize = isSpecialSku ? namedSizeFor(width, length) : undefined;
  if (namedSize) {
    return (
      <>
        <span style={{ paddingRight: '4px' }}>{namedSize}</span>
        <span>{`(${shown(width)}`}</span>
        <small>W</small>
        {separator}
        <span>{shown(length)}</span>
        <small>L)</small>
      </>
    );
  }

  return (
    <>
      <span>{shown(width)}</span>
      <small>W</small>
      {separator}
      <span>{shown(length)}</span>
      <small>L</small>
      {isCube && (
        <>
          {separator}
          <span>{shown(height)}</span>
          <small>H</small>
        </>
      )}
    </>
  );
};
